import { useEffect, useState, useCallback } from "react";
import { load } from "@cashfreepayments/cashfree-js";
import api, { extractErr } from "@/lib/api";
import { useMerchantAuth } from "@/lib/auth";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, Loader2, LogOut, ShieldCheck, Smartphone, Globe } from "lucide-react";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function RenewGate() {
  const { user, tenant, logout, refresh } = useMerchantAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/merchant/renewal");
      setData(data);
    } catch (e) {
      toast.error(extractErr(e));
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cfOrderId = params.get("cf_order_id");
    const run = async () => {
      if (cfOrderId && cfOrderId.startsWith("ren_")) {
        setVerifying(true);
        try {
          const { data } = await api.get(`/merchant/renewal/verify/${cfOrderId}`);
          if (data.renewed) {
            toast.success("Plan renewed — welcome back!");
            params.delete("cf_order_id");
            window.history.replaceState({}, "", `${window.location.pathname}`);
            await refresh(); // reload subscription -> unlocks the panel
            return;
          }
          toast.error("Payment not completed yet.");
        } catch (e) {
          toast.error(extractErr(e));
        } finally {
          setVerifying(false);
        }
      }
      await loadStatus();
    };
    run();
  }, [loadStatus, refresh]);

  const renew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/merchant/renewal/checkout");
      const cashfree = await load({ mode: data.sdk_mode || "sandbox" });
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (e) {
      toast.error(extractErr(e));
      setBusy(false);
    }
  };

  const doLogout = async () => { await logout(); toast.success("Signed out"); window.location.href = "/login"; };

  const p = data?.pricing || {};
  const sub = data?.subscription || tenant?.subscription || {};
  const isApp = p.plan_id === "website_app" || p.plan_id === "website_app_ios" || sub.plan_id === "website_app" || sub.plan_id === "website_app_ios";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="renew-gate">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6"><Brand size="md" /></div>
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-900 text-white p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-500/20 border border-red-400/40 flex items-center justify-center mx-auto"><Lock className="w-5 h-5 text-red-300" /></div>
            <h1 className="mt-4 font-display text-2xl font-bold">Your plan has expired</h1>
            <p className="mt-1 text-sm text-slate-300">Your store & admin panel are paused. Renew to bring everything back online instantly.</p>
          </div>

          <div className="p-6 space-y-5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Store</span>
              <span className="font-semibold text-slate-800">{tenant?.name || "—"}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Plan</span>
              <span className="inline-flex items-center gap-1.5 font-semibold text-slate-800">
                {isApp ? <Smartphone className="w-4 h-4 text-fuchsia-500" /> : <Globe className="w-4 h-4 text-sky-500" />}
                {isApp ? "Website + App" : "Website"}
              </span>
            </div>
            {sub.expires_at && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Expired on</span>
                <span className="font-mono-nums text-red-600">{new Date(sub.expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
              </div>
            )}

            {/* Price breakdown with GST */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2" data-testid="renew-breakdown">
              <div className="flex items-center justify-between text-sm text-slate-600">
                <span>{isApp ? "Website + App plan (1 year)" : "Website plan (1 year)"}</span>
                <span className="font-mono-nums">{INR(p.base_amount)}</span>
              </div>
              <div className="flex items-center justify-between text-sm text-slate-600">
                <span>GST ({p.gst_percent ?? 18}%)</span>
                <span className="font-mono-nums" data-testid="renew-gst">{INR(p.gst_amount)}</span>
              </div>
              <div className="border-t border-slate-200 pt-2 flex items-center justify-between">
                <span className="font-semibold text-slate-900">Total payable</span>
                <span className="font-display text-xl font-bold text-slate-900 font-mono-nums" data-testid="renew-total">{INR(p.total)}</span>
              </div>
              {p.gstin && (
                <div className="pt-1 text-[11px] text-slate-400">GSTIN {p.gstin}{p.company ? ` · ${p.company}` : ""}</div>
              )}
            </div>

            <Button onClick={renew} disabled={busy || verifying || !data?.cashfree_configured} data-testid="renew-pay-btn" className="w-full bg-blue-600 hover:bg-blue-700 h-11 text-base">
              {verifying ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying payment…</>) : busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening checkout…</>) : (<><ShieldCheck className="w-4 h-4 mr-2" /> Renew now · {INR(p.total)}</>)}
            </Button>
            {data && !data.cashfree_configured && (
              <p className="text-xs text-amber-600 text-center">Payment gateway is not configured. Please contact support.</p>
            )}

            <div className="flex items-center justify-between text-xs text-slate-400 pt-1">
              <span className="truncate">{user?.email}</span>
              <button onClick={doLogout} data-testid="renew-logout" className="inline-flex items-center gap-1 hover:text-slate-700"><LogOut className="w-3 h-3" /> Sign out</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
